import { createContext, useCallback, useContext, useEffect } from "react";
import { useAuth as useWorkOSAuth } from "@workos-inc/authkit-react";
import { getTrialStatus } from "@/shared/lib/trial";
import type { TrialStatus } from "@/shared/lib/trial";
import { useAnalyticsIdentity } from "@/shared/auth/useAnalyticsIdentity";
import { useUserAccountData } from "@/shared/auth/useUserAccountData";
import { setSentryUser } from "@/shared/lib/sentry";
import type {
  BrandSettings,
  SenderIdentity,
  UserTrialData,
} from "@/shared/types/account";
export type {
  BrandSettings,
  SenderIdentity,
  UserAccountData,
  UserTrialData,
} from "@/shared/types/account";

interface ExtendedAuthContextValue {
  // WorkOS auth state
  user: ReturnType<typeof useWorkOSAuth>["user"];
  isLoading: boolean;
  signIn: ReturnType<typeof useWorkOSAuth>["signIn"];
  signOut: ReturnType<typeof useWorkOSAuth>["signOut"];
  // Plan state (trial retired with PK-235: free or subscribed)
  trialStatus: TrialStatus;
  isSubscribed: boolean;
  userTrialData: UserTrialData | null;
  sender: SenderIdentity;
  brand: BrandSettings;
  refetchUserData: () => Promise<void>;
  setSender: (sender: SenderIdentity) => void;
  setBrand: (brand: BrandSettings) => void;
  // Default language for new proposals (PK-293).
  proposalLanguage: string;
  setProposalLanguage: (code: string) => void;
  // Default cover photo for new proposals (PK-296).
  coverImageUrl: string | null;
  setCoverImageUrl: (url: string | null) => void;
}

const ExtendedAuthContext = createContext<ExtendedAuthContextValue | null>(null);

export function ExtendedAuthProvider({ children }: { children: React.ReactNode }) {
  const workosAuth = useWorkOSAuth();
  const { user, isLoading: workosLoading, signIn, signOut, getAccessToken } = workosAuth;
  const {
    userData,
    isLoading: isLoadingAccountData,
    setUserData,
    refetchUserData,
  } = useUserAccountData({
    user,
    getAccessToken,
  });

  useAnalyticsIdentity(userData);

  useEffect(() => {
    setSentryUser(userData?.id ?? null);
  }, [userData?.id]);

  // Compute plan status. When userData is missing post-load (e.g. /me failed
  // in a stale tab), report "unknown" rather than "free" — the latter would
  // surface a fake plan banner on top of the real fetch error.
  const trialStatus: TrialStatus = userData
    ? getTrialStatus(userData)
    : "unknown";

  const sender: SenderIdentity = {
    business_name: userData?.business_name ?? null,
    contact_email: userData?.contact_email ?? null,
    contact_phone: userData?.contact_phone ?? null,
    website_url: userData?.website_url ?? null,
  };

  const brand: BrandSettings = {
    tagline: userData?.tagline ?? null,
    logo_url: userData?.logo_url ?? null,
    primary_color: userData?.primary_color ?? null,
    accent_color: userData?.accent_color ?? null,
    heading_font: userData?.heading_font ?? null,
    body_font: userData?.body_font ?? null,
    brand_strength: userData?.brand_strength ?? null,
  };

  const setSender = useCallback((next: SenderIdentity) => {
    setUserData((prev) => (prev ? { ...prev, ...next } : prev));
  }, [setUserData]);

  const setBrand = useCallback((next: BrandSettings) => {
    setUserData((prev) => (prev ? { ...prev, ...next } : prev));
  }, [setUserData]);

  const setProposalLanguage = useCallback((code: string) => {
    setUserData((prev) => (prev ? { ...prev, proposal_language: code } : prev));
  }, [setUserData]);

  const setCoverImageUrl = useCallback((url: string | null) => {
    setUserData((prev) => (prev ? { ...prev, cover_image_url: url } : prev));
  }, [setUserData]);

  const value: ExtendedAuthContextValue = {
    user,
    isLoading: workosLoading || isLoadingAccountData,
    signIn,
    signOut,
    trialStatus,
    isSubscribed: trialStatus === "subscribed",
    userTrialData: userData,
    sender,
    brand,
    refetchUserData,
    setSender,
    setBrand,
    proposalLanguage: userData?.proposal_language ?? "en",
    setProposalLanguage,
    coverImageUrl: userData?.cover_image_url ?? null,
    setCoverImageUrl,
  };

  return (
    <ExtendedAuthContext.Provider value={value}>
      {children}
    </ExtendedAuthContext.Provider>
  );
}

export function useExtendedAuth() {
  const context = useContext(ExtendedAuthContext);
  if (!context) {
    throw new Error("useExtendedAuth must be used within an ExtendedAuthProvider");
  }
  return context;
}
